import { useState, useEffect, useCallback } from "react";
import { useErs } from "src/context/ers/ers.provider";
import { isPayloadSource } from "src/utils/hubSource";

/**
 * Unified ERS-or-Payload hub loader.
 *
 * @param {string} moduleName - e.g. `promotions`, `entertainment`
 * @param {Object} config
 * @param {(locale: string) => Promise<Object[]>} config.fetchPayloadData - Fetches this module's cards from Payload
 * @param {string} config.ersKeyPrefix - ERS cache key prefix; the ERS entry is looked up as `${ersKeyPrefix}-${locale}`
 * @param {string} locale - `en` | `fr`
 * @returns {{
 *   data: Object[] | undefined,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useHubData(moduleName, config, locale) {
  const { fetchPayloadData, ersKeyPrefix } = config;
  const usePayload = isPayloadSource(moduleName);
  const ersKey = `${ersKeyPrefix}-${locale}`;

  const { data: ersData, fetchData, error: ersError } = useErs();

  const [payloadData, setPayloadData] = useState([]);
  const [payloadLoading, setPayloadLoading] = useState(usePayload);
  const [payloadError, setPayloadError] = useState(null);
  const [ersLoading, setErsLoading] = useState(!usePayload);

  const fetchPayload = useCallback(async () => {
    if (!usePayload) return;

    setPayloadLoading(true);
    setPayloadError(null);

    try {
      const cards = await fetchPayloadData(locale);
      setPayloadData(cards);
    } catch (err) {
      console.error(`Error fetching ${moduleName} from Payload:`, err);
      setPayloadError(err?.message || `Failed to load ${moduleName}`);
      setPayloadData([]);
    } finally {
      setPayloadLoading(false);
    }
  }, [locale, usePayload, fetchPayloadData, moduleName]);

  useEffect(() => {
    fetchPayload();
  }, [fetchPayload]);

  useEffect(() => {
    if (usePayload) return;

    setErsLoading(true);

    fetchData(ersKey)
      .catch((err) => {
        console.error(`Error fetching ${moduleName} from ERS:`, err);
      })
      .finally(() => {
        setErsLoading(false);
      });
  }, [usePayload, fetchData, ersKey, moduleName]);

  if (usePayload) {
    return {
      data: payloadData,
      loading: payloadLoading,
      error: payloadError,
    };
  }

  return {
    data: ersData[ersKey],
    loading: ersLoading,
    error: ersError,
  };
}
